import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { IconButton } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import { LoadingSmall } from '../../loading/loadingPage'
import KPI_SECTIONS from './sections'

// Per-tenant KPI target overrides. Empty field = use the default.
const KPITargetsSettings = () => {
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)
  const [targets, setTargets] = useState(null)
  const [values, setValues] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)
  const [helpTarget, setHelpTarget] = useState(null)

  useEffect(() => {
    API.get('/kpi-dashboard/targets')
      .then((res) => {
        if (res.status === 200) {
          setTargets(res.data.targets)
          setValues(
            Object.fromEntries(
              res.data.targets
                .filter((target) => target.override !== null)
                .map((target) => [target.key, String(target.override)]),
            ),
          )
        } else setError(true)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [])

  const handleSave = async () => {
    setSaving(true)
    const payload = Object.fromEntries(
      Object.entries(values).filter(([, value]) => value !== '' && value !== null),
    )
    const res = await API.put('/kpi-dashboard/targets', { targets: payload })
    setSaving(false)
    if (res.status === 200) {
      setToast({ message: 'KPI targets saved' })
    } else {
      setToast({ message: res.data?.error ?? 'Unable to save targets', severity: 'error' })
    }
  }

  const sectionsWithTargets = KPI_SECTIONS.map((section) => ({
    ...section,
    targets: (targets ?? []).filter((target) => target.section === section.slug),
  })).filter((section) => section.targets.length)

  return (
    <Stack gap={1} padding={1}>
      <Stack direction="row" alignItems="center" gap={1}>
        <IconButton onClick={() => navigate('/kpi')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="title2">KPI Target Settings</Typography>
      </Stack>
      <Alert severity="info">
        Targets set here apply to every KPI dashboard for your organisation. Leave a field blank to
        use the default. For &quot;lower is better&quot; KPIs the target is a maximum.
      </Alert>
      {error && <Alert severity="error">Unable to load KPI targets.</Alert>}
      {loading ? (
        <LoadingSmall />
      ) : (
        sectionsWithTargets.map((section) => (
          <Paper key={section.slug} sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="title3" sx={{ mb: 1 }}>
              {section.name}
            </Typography>
            <Grid container spacing={2}>
              {section.targets.map((target) => (
                <Grid item xs={12} sm={6} md={4} key={target.key}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={target.label}
                    value={values[target.key] ?? ''}
                    placeholder={String(target.defaultTarget)}
                    helperText={`Default: ${target.reverse ? '≤' : '≥'} ${target.defaultTarget}%`}
                    onChange={(e) =>
                      setValues((previous) => ({ ...previous, [target.key]: e.target.value }))
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          %{' '}
                          {target.reverse ? (
                            <Chip size="small" label="max" sx={{ ml: 0.5 }} />
                          ) : null}
                          <IconButton
                            size="small"
                            onClick={() => setHelpTarget(target)}
                            sx={{ ml: 0.5 }}
                          >
                            <HelpOutlineIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        ))
      )}
      {!loading && !error && (
        <Stack direction="row" gap={1} justifyContent="flex-end">
          <Button startIcon={<RestartAltIcon />} onClick={() => setValues({})} color="inherit">
            Reset all to defaults
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save targets'}
          </Button>
        </Stack>
      )}
      <Dialog open={!!helpTarget} onClose={() => setHelpTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{helpTarget?.label}</DialogTitle>
        <DialogContent>
          <Stack gap={1.5}>
            <Typography variant="body2">{helpTarget?.description}</Typography>
            <Typography variant="body2" color="text.secondary">
              {helpTarget?.reverse
                ? `This is a "lower is better" KPI, so the target is a maximum: performance is on target when the value is at or below it. The default is ${helpTarget?.defaultTarget}%.`
                : `Performance is on target when the value is at or above the target. The default is ${helpTarget?.defaultTarget}%.`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter a value between 0 and 100 to override the default for your organisation, or
              leave the field blank to use the default. Changes apply to the dashboards and PDF
              reports as soon as they are saved.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default KPITargetsSettings
